import type {
  AccountCustodyName,
  AccountSubtypeName,
  AssetClassName,
  PortfolioItemKind,
  PortfolioItemTypeName,
  ProviderAccountSource,
} from 'src/gql/graphql';
import { isNil } from 'es-toolkit';
import { DateTime } from 'luxon';
import { ASSET_CLASS_DISPLAY_NAMES, CLIENT_LINKED_PROVIDER_ACCOUNT_SOURCES } from 'src/constants/account-link';
import { getHeldawayAccountURI, getManagedAccountURI } from 'src/utils/account';
import { knownCustodianLogoUrl } from 'src/utils/custodian-logo';
import { liabilitySubtitle } from 'src/utils/liability';

import { accountSubtypeDisplayName } from '@savvy/deprecated-shared/utils/portfolio';

import type { AccountSleeveInput } from './account-sleeves';
import type { AccountGroupId } from './constants';
import type { AccountGroup, AccountRow, StaleConnection, StaleConnectionReason } from './types';
import { csvCell, csvDate, csvValue } from '../common/csv';
import { isPresent, toFiniteNumber } from '../common/utils';
import { accountSleeveLines } from './account-sleeves';
import {
  ACCOUNT_GROUPS,
  ACCOUNT_MASK_PREFIX,
  ACCOUNT_TYPE_TO_GROUP_ID,
  ACCOUNTS_BALANCES_UPDATED_PREFIX,
  ASSET_CLASS_TO_GROUP_ID,
  LIABILITY_GROUP_IDS,
  NET_WORTH_CSV_HEADER,
  ROW_UPDATED_PREFIX,
  STALE_UNKNOWN_INSTITUTION_LABEL,
  TRANSFER_VISIBLE_EMPTY_GROUP_IDS,
} from './constants';

type VisibilityFields = {
  readonly hiddenFromClient?: boolean | null;
  readonly hiddenFromNetWorth?: boolean | null;
};

export type PortfolioAccountInput = VisibilityFields & {
  readonly id: string;
  readonly accountName: string;
  readonly accountNumber?: string | null;
  readonly accountSleeves?: readonly AccountSleeveInput[] | null;
  readonly custody: AccountCustodyName;
  readonly customName?: string | null;
  readonly displayBalance: { readonly amount: string };
  readonly firmName?: string | null;
  readonly lastUpdatedAt?: string | null;
  readonly mask?: string | null;
  readonly portfolio?: { readonly isPersonal: boolean } | null;
  readonly providerAccount?: {
    readonly providerName?: string | null;
    readonly source?: ProviderAccountSource | null;
  } | null;
  readonly providerAccountId?: string | null;
  readonly providerLogoUrl?: string | null;
  readonly subtype?: AccountSubtypeName | null;
  readonly type: PortfolioItemTypeName;
};

export type PortfolioAssetInput = VisibilityFields & {
  readonly id: string;
  readonly assetClass: AssetClassName;
  readonly description: string;
  readonly lastUpdatedAt?: string | null;
  readonly netValue: { readonly amount: string };
};

export type PortfolioLiabilityInput = VisibilityFields & {
  readonly id: string;
  readonly currentBalance: { readonly amount: string };
  readonly lastUpdatedAt?: string | null;
  readonly name: string;
  readonly providerName?: string | null;
  readonly type: PortfolioItemTypeName;
};

export type PortfolioItemsInput = {
  readonly accounts: readonly PortfolioAccountInput[];
  readonly assets: readonly PortfolioAssetInput[];
  readonly liabilities: readonly PortfolioLiabilityInput[];
};

function isLiabilityGroup(groupId: AccountGroupId) {
  return LIABILITY_GROUP_IDS.includes(groupId);
}

function withoutNegativeZero(amount: number) {
  return amount === 0 ? 0 : amount;
}

function signedBalance(storedAmount: string, groupId: AccountGroupId) {
  const amount = toFiniteNumber(storedAmount);
  if (isNil(amount)) {
    return null;
  }
  return withoutNegativeZero(isLiabilityGroup(groupId) ? -amount : amount);
}

export function signedAccountAmount(storedAmount: string, type: PortfolioItemTypeName) {
  return signedBalance(storedAmount, ACCOUNT_TYPE_TO_GROUP_ID[type]);
}

export function isManagedBySavvy(custody: AccountCustodyName) {
  return custody === 'managed';
}

export function accountDetailHref(account: { readonly id: string; readonly custody: AccountCustodyName }) {
  return isManagedBySavvy(account.custody)
    ? getManagedAccountURI(account.id)
    : getHeldawayAccountURI({ id: account.id }, false, null);
}

export function portfolioItemDetailHref(item: { readonly id: string; readonly kind: PortfolioItemKind }) {
  return item.kind === 'asset' ? `/portfolio/assets/${item.id}` : `/portfolio/liabilities/${item.id}`;
}

export function accountMaskLabel(mask: string | null | undefined) {
  return isPresent(mask) ? `${ACCOUNT_MASK_PREFIX}${mask}` : null;
}

export function revealableAccountNumber(accountNumber: string | null | undefined) {
  return isPresent(accountNumber) && !accountNumber.includes('*') ? accountNumber : null;
}

export function accountTypeLabel(account: Pick<PortfolioAccountInput, 'subtype'>) {
  if (!isNil(account.subtype) && account.subtype !== 'other') {
    return accountSubtypeDisplayName(account.subtype);
  }
  return null;
}

export function accountCustomName(account: Pick<PortfolioAccountInput, 'customName'>) {
  return isPresent(account.customName) ? account.customName : null;
}

export function accountDisplayName(account: Pick<PortfolioAccountInput, 'accountName' | 'customName'>) {
  return accountCustomName(account) ?? account.accountName;
}

function accountRow(account: PortfolioAccountInput, groupId: AccountGroupId): AccountRow {
  const institution = account.providerAccount?.providerName ?? account.firmName ?? null;
  return {
    id: account.id,
    kind: 'account',
    name: accountDisplayName(account),
    sourceName: account.accountName,
    customName: accountCustomName(account),
    typeLabel: accountTypeLabel(account),
    institution,
    mask: account.mask ?? null,
    accountNumber: revealableAccountNumber(account.accountNumber),
    logoUrl: knownCustodianLogoUrl(institution) ?? account.providerLogoUrl ?? null,
    managedBySavvy: isManagedBySavvy(account.custody),
    heldAway: account.custody === 'held_away',
    providerLinked: !isNil(account.providerAccountId),
    countsTowardNetWorth: countsTowardNetWorth(account),
    href: accountDetailHref(account),
    balance: signedBalance(account.displayBalance.amount, groupId),
    lastUpdatedAt: account.lastUpdatedAt ?? null,
    sleeves: accountSleeveLines(account.accountSleeves),
  };
}

function assetRow(asset: PortfolioAssetInput, groupId: AccountGroupId): AccountRow {
  return {
    id: asset.id,
    kind: 'asset',
    name: asset.description,
    sourceName: asset.description,
    customName: null,
    typeLabel: ASSET_CLASS_DISPLAY_NAMES[asset.assetClass],
    institution: null,
    mask: null,
    accountNumber: null,
    logoUrl: null,
    managedBySavvy: false,
    heldAway: false,
    providerLinked: false,
    countsTowardNetWorth: countsTowardNetWorth(asset),
    href: portfolioItemDetailHref({ id: asset.id, kind: 'asset' }),
    balance: signedBalance(asset.netValue.amount, groupId),
    lastUpdatedAt: asset.lastUpdatedAt ?? null,
    sleeves: [],
  };
}

function liabilityRow(liability: PortfolioLiabilityInput, groupId: AccountGroupId): AccountRow {
  return {
    id: liability.id,
    kind: 'liability',
    name: liability.name,
    sourceName: liability.name,
    customName: null,
    typeLabel: liabilitySubtitle(liability.type),
    institution: liability.providerName ?? null,
    mask: null,
    accountNumber: null,
    logoUrl: knownCustodianLogoUrl(liability.providerName),
    managedBySavvy: false,
    heldAway: false,
    providerLinked: false,
    countsTowardNetWorth: countsTowardNetWorth(liability),
    href: portfolioItemDetailHref({ id: liability.id, kind: 'liability' }),
    balance: signedBalance(liability.currentBalance.amount, groupId),
    lastUpdatedAt: liability.lastUpdatedAt ?? null,
    sleeves: [],
  };
}

function liabilityGroupId(type: PortfolioItemTypeName): AccountGroupId {
  return type === 'credit' ? 'credit' : 'loans';
}

function isVisibleToClient(item: VisibilityFields) {
  return item.hiddenFromClient !== true;
}

function isManagedOutsidePersonalPortfolio(account: PortfolioAccountInput) {
  return account.type === 'investment' && account.custody === 'managed' && account.portfolio?.isPersonal !== true;
}

function countsTowardNetWorth(item: VisibilityFields) {
  return item.hiddenFromNetWorth !== true;
}

function groupTotal(rows: readonly AccountRow[]) {
  const counted = rows.filter((row) => row.countsTowardNetWorth);
  if (counted.length === 0) {
    return 0;
  }
  const populated = counted.filter((row) => !isNil(row.balance));
  if (populated.length === 0) {
    return null;
  }
  return populated.reduce((total, row) => total + (row.balance ?? 0), 0);
}

function pushRow(rowsByGroup: Map<AccountGroupId, AccountRow[]>, groupId: AccountGroupId, row: AccountRow) {
  const existing = rowsByGroup.get(groupId);
  if (existing) {
    existing.push(row);
    return;
  }
  rowsByGroup.set(groupId, [row]);
}

export function accountGroups(portfolio: PortfolioItemsInput | undefined): readonly AccountGroup[] {
  if (isNil(portfolio)) {
    return [];
  }
  const rowsByGroup = new Map<AccountGroupId, AccountRow[]>();

  portfolio.accounts
    .filter((account) => isVisibleToClient(account) && !isManagedOutsidePersonalPortfolio(account))
    .forEach((account) => {
      const groupId = ACCOUNT_TYPE_TO_GROUP_ID[account.type];
      pushRow(rowsByGroup, groupId, accountRow(account, groupId));
    });

  portfolio.assets.filter(isVisibleToClient).forEach((asset) => {
    const groupId = ASSET_CLASS_TO_GROUP_ID[asset.assetClass];
    pushRow(rowsByGroup, groupId, assetRow(asset, groupId));
  });

  portfolio.liabilities.filter(isVisibleToClient).forEach((liability) => {
    const groupId = liabilityGroupId(liability.type);
    pushRow(rowsByGroup, groupId, liabilityRow(liability, groupId));
  });

  return ACCOUNT_GROUPS.flatMap((group) => {
    const rows = rowsByGroup.get(group.id);
    if (isNil(rows) || rows.length === 0) {
      return [];
    }
    const sortedRows = [...rows].sort((left, right) => Math.abs(right.balance ?? 0) - Math.abs(left.balance ?? 0));
    return [
      {
        id: group.id,
        label: group.label,
        connectLabel: group.connectLabel,
        connectItemType: group.connectItemType,
        connectFeedOnly: group.connectFeedOnly === true,
        rows: sortedRows,
        total: groupTotal(sortedRows),
      },
    ];
  });
}

export function clientLinkedAccountCount(portfolio: PortfolioItemsInput | undefined): number {
  if (isNil(portfolio)) {
    return 0;
  }
  return portfolio.accounts.filter((account) => {
    const source = account.providerAccount?.source;
    return !isNil(source) && CLIENT_LINKED_PROVIDER_ACCOUNT_SOURCES.includes(source);
  }).length;
}

export function shouldShowConnectNudge({
  transferring,
  canConnect,
  dismissed,
  portfolio,
}: {
  readonly transferring: boolean;
  readonly canConnect: boolean;
  readonly dismissed: boolean;
  readonly portfolio: PortfolioItemsInput | undefined;
}): boolean {
  if (!transferring || !canConnect || dismissed || isNil(portfolio)) {
    return false;
  }
  return clientLinkedAccountCount(portfolio) === 0;
}

export function transferAccountSections(groups: readonly AccountGroup[]): readonly AccountGroup[] {
  const byId = new Map(groups.map((group) => [group.id, group]));

  return ACCOUNT_GROUPS.flatMap((definition) => {
    const group = byId.get(definition.id);
    if (!isNil(group)) {
      return [group];
    }
    if (!TRANSFER_VISIBLE_EMPTY_GROUP_IDS.includes(definition.id)) {
      return [];
    }
    return [
      {
        id: definition.id,
        label: definition.label,
        connectLabel: definition.connectLabel,
        connectItemType: definition.connectItemType,
        connectFeedOnly: definition.connectFeedOnly === true,
        rows: [],
        total: null,
      },
    ];
  });
}

export function canUpdateAccountBalance(row: AccountRow) {
  return row.kind === 'account' && !row.managedBySavvy && !row.providerLinked;
}

// A liability-group row shows a negated balance, but `current_balance` is stored unsigned -- a card
// reading -$4,210 on screen is 4210 in the column the client edits.
export function accountBalanceDraft(balance: number | null) {
  return isNil(balance) ? '' : String(Math.abs(balance));
}

export function removeAccountTitle(row: AccountRow) {
  return `Remove ${row.name}?`;
}

export function removeAccountDescription(row: AccountRow) {
  const custodian = isNil(row.institution) || row.institution === '' ? null : row.institution;
  if (isNil(custodian)) {
    return 'This unlinks the account from your portal. Your balances and history stay with your institution — you can reconnect it anytime.';
  }
  return `This unlinks the account from your portal. Your balances and history stay with ${custodian} — you can reconnect it anytime.`;
}

export function netWorthFromGroups(groups: readonly AccountGroup[]) {
  return groups.reduce((sum, group) => sum + (group.total ?? 0), 0);
}

function providerFedRows(groups: readonly AccountGroup[]) {
  return groups.flatMap((group) => group.rows).filter((row) => row.kind === 'account');
}

export function balancesUpdatedAt(groups: readonly AccountGroup[]): string | null {
  const stamps = providerFedRows(groups).flatMap((row) => {
    if (isNil(row.lastUpdatedAt)) {
      return [];
    }
    const parsed = DateTime.fromISO(row.lastUpdatedAt);
    return parsed.isValid ? [{ iso: row.lastUpdatedAt, millis: parsed.toMillis() }] : [];
  });

  const latest = stamps.reduce<{ iso: string; millis: number } | null>(
    (winner, stamp) => (isNil(winner) || stamp.millis > winner.millis ? stamp : winner),
    null,
  );
  return latest?.iso ?? null;
}

export function netWorthPointDateLabel(isoDate: string) {
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toFormat('LLL d, yyyy') : isoDate;
}

export function rowUpdatedLabel(isoDateTime: string | null, now: DateTime = DateTime.now()) {
  if (isNil(isoDateTime)) {
    return null;
  }
  const parsed = DateTime.fromISO(isoDateTime);
  const relative = parsed.isValid ? parsed.toRelative({ base: now }) : null;
  return isNil(relative) ? null : `${ROW_UPDATED_PREFIX} ${relative}`;
}

export function balancesUpdatedLabel(isoDateTime: string | null, now: DateTime = DateTime.now()): string | null {
  if (isNil(isoDateTime)) {
    return null;
  }
  const updated = DateTime.fromISO(isoDateTime).setZone(now.zone);
  if (!updated.isValid) {
    return null;
  }

  const time = updated.toFormat('h:mm a');
  const days = Math.round(now.startOf('day').diff(updated.startOf('day'), 'days').days);
  if (days <= 0) {
    return `${ACCOUNTS_BALANCES_UPDATED_PREFIX} today at ${time}`;
  }
  if (days === 1) {
    return `${ACCOUNTS_BALANCES_UPDATED_PREFIX} yesterday at ${time}`;
  }
  return `${ACCOUNTS_BALANCES_UPDATED_PREFIX} ${updated.toFormat('LLL d')} at ${time}`;
}

type FreshnessInput = {
  readonly syncStatus: string;
  readonly lastSyncedAt?: string | null;
  readonly linkStatus: string;
  readonly disconnected: boolean;
  readonly reconnectSupported: boolean;
};

export type FreshnessProviderAccountInput = {
  readonly id: string;
  readonly providerName?: string | null;
  readonly source: string;
  readonly freshness: FreshnessInput;
};

export type FreshnessAccountInput = VisibilityFields & {
  readonly firmName?: string | null;
  readonly providerAccount?: FreshnessProviderAccountInput | null;
};

export type FreshnessPortfolioInput = {
  readonly accounts: readonly FreshnessAccountInput[];
  readonly clientProfile?: {
    readonly providerAccounts?: readonly FreshnessProviderAccountInput[] | null;
  } | null;
};

const SIGN_IN_LINK_STATUSES: readonly string[] = ['ITEM_LOGIN_REQUIRED', 'PASSWORD_RESET_REQUIRED'];

// A connection that has never delivered any accounts. The direct-enumeration
// pass surfaces only these, so a link whose rows exist but are filtered from
// `accounts` (soft-deleted, to_be_opened, or advisor-hidden) is not mistaken
// for a genuinely empty broken link and does not raise a spurious banner.
const NEVER_SYNCED_STATUS = 'NEVER_SYNCED';

function staleConnectionReason(freshness: {
  readonly syncStatus: string;
  readonly disconnected: boolean;
}): StaleConnectionReason | null {
  if (freshness.disconnected) {
    return 'disconnected';
  }
  return freshness.syncStatus === 'STALE' ? 'delayed-feed' : null;
}

const STALE_CONNECTION_REASON_ORDER: readonly StaleConnectionReason[] = ['disconnected', 'delayed-feed'];

export function staleConnections(portfolio: FreshnessPortfolioInput | undefined): readonly StaleConnection[] {
  if (isNil(portfolio)) {
    return [];
  }
  const byProviderAccount = new Map<string, StaleConnection>();
  // Provider accounts that already surfaced through an account row, so the
  // direct-enumeration pass below does not double-add them.
  const providerAccountIdsWithAccounts = new Set(
    portfolio.accounts.map((account) => account.providerAccount?.id).filter((id): id is string => !isNil(id)),
  );

  portfolio.accounts.filter(isVisibleToClient).forEach((account) => {
    const providerAccount = account.providerAccount;
    if (isNil(providerAccount) || byProviderAccount.has(providerAccount.id)) {
      return;
    }
    const reason = staleConnectionReason(providerAccount.freshness);
    if (isNil(reason)) {
      return;
    }
    byProviderAccount.set(providerAccount.id, {
      id: providerAccount.id,
      institution: providerAccount.providerName ?? account.firmName ?? null,
      source: providerAccount.source,
      reason,
      requiresSignIn: SIGN_IN_LINK_STATUSES.includes(providerAccount.freshness.linkStatus),
      lastSyncedAt: providerAccount.freshness.lastSyncedAt ?? null,
      reconnectSupported: providerAccount.freshness.reconnectSupported,
    });
  });

  // A link that delivered zero accounts never appears in `accounts`, so the loop
  // above cannot surface it — the client is left with a silently broken
  // connection and no reconnect prompt. Enumerate provider accounts directly and
  // add the ones that have genuinely never synced an account, so an empty/broken
  // link still raises a banner. Gating on NEVER_SYNCED is what separates a
  // truly-empty link from one whose rows are merely filtered out of `accounts`
  // (soft-deleted, to_be_opened, or advisor-hidden), which must not raise a
  // banner. Institution name must come from the provider account here; there is
  // no account row to fall back on for firmName.
  portfolio.clientProfile?.providerAccounts?.forEach((providerAccount) => {
    if (providerAccountIdsWithAccounts.has(providerAccount.id) || byProviderAccount.has(providerAccount.id)) {
      return;
    }
    if (providerAccount.freshness.syncStatus !== NEVER_SYNCED_STATUS) {
      return;
    }
    const reason = staleConnectionReason(providerAccount.freshness);
    if (isNil(reason)) {
      return;
    }
    byProviderAccount.set(providerAccount.id, {
      id: providerAccount.id,
      institution: providerAccount.providerName ?? null,
      source: providerAccount.source,
      reason,
      requiresSignIn: SIGN_IN_LINK_STATUSES.includes(providerAccount.freshness.linkStatus),
      lastSyncedAt: providerAccount.freshness.lastSyncedAt ?? null,
      reconnectSupported: providerAccount.freshness.reconnectSupported,
    });
  });

  return [...byProviderAccount.values()].sort((left, right) => {
    const byReason =
      STALE_CONNECTION_REASON_ORDER.indexOf(left.reason) - STALE_CONNECTION_REASON_ORDER.indexOf(right.reason);
    return byReason === 0 ? left.id.localeCompare(right.id) : byReason;
  });
}

export function staleConnectionsSignature(connections: readonly StaleConnection[]) {
  return connections.map((connection) => `${connection.id}:${connection.reason}`).join(',');
}

export function institutionLabel(connection: StaleConnection) {
  return isPresent(connection.institution) ? connection.institution : STALE_UNKNOWN_INSTITUTION_LABEL;
}

export function institutionListLabel(connections: readonly StaleConnection[]) {
  const names = connections.map(institutionLabel);
  if (names.length <= 2) {
    return names.join(' and ');
  }
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}

export function accountFileSlug(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return slug === '' ? 'account' : slug;
}

export function accountsRowsToCsv(groups: readonly AccountGroup[]): string {
  const lines = groups.flatMap((group) =>
    group.rows.map((row) =>
      [
        group.label,
        row.name,
        row.typeLabel ?? '',
        row.institution ?? '',
        accountMaskLabel(row.mask) ?? '',
        row.countsTowardNetWorth ? 'Yes' : 'No',
        csvValue(row.balance),
        csvDate(row.lastUpdatedAt),
      ]
        .map(csvCell)
        .join(','),
    ),
  );
  return [NET_WORTH_CSV_HEADER.map(csvCell).join(','), ...lines].join('\n');
}

export function lastSyncedLabel(isoDateTime: string | null, now: DateTime = DateTime.now()): string | null {
  if (isNil(isoDateTime)) {
    return null;
  }
  const synced = DateTime.fromISO(isoDateTime).setZone(now.zone);
  if (!synced.isValid) {
    return null;
  }
  const days = Math.round(now.startOf('day').diff(synced.startOf('day'), 'days').days);
  if (days <= 0) {
    return `today at ${synced.toFormat('h:mm a')}`;
  }
  if (days === 1) {
    return `yesterday at ${synced.toFormat('h:mm a')}`;
  }
  return synced.hasSame(now, 'year') ? synced.toFormat('LLLL d') : synced.toFormat('LLLL d, yyyy');
}
